import React, { useState, useContext } from 'react';
import { Box, Typography, Button, Grid } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Screen } from '../../components/Screen';
import ThemeOptions from '../OrganizationOnboardingScreen/components/ThemeOptions';
import CustomizationOptions from '../OrganizationOnboardingScreen/components/CustomizationOptions';
import SignUpOptions from '../OrganizationOnboardingScreen/components/SignUpOptions';
import CompletionOptions from '../OrganizationOnboardingScreen/components/CompletionOptions';
import ControlledAccordion from '../OrganizationOnboardingScreen/components/ControlledAccordion';
import { PartnerContext } from '../../lib/contexts/PartnerContext';

const PartnerSettingsScreen = () => {
  const theme = useTheme();
  const { state: partnerObject } = useContext(PartnerContext);
  // console.log('here is partnerObject: ', partnerObject);

  const [formData, setFormData] = useState({
    // Theme Options
    primaryColor: theme.palette.primary.main,
    secondaryColor: theme.palette.secondary.main,
    tertiaryColor: '',
    borderWidth: '',
    borderRadius: '',
    borderColor: '',
    boxShadow: '',

    // Customization Options
    customizationOptions: {
      useMetricSystem: false,
      showPoweredBy: false,
      skipHowItWorks: false,
      customMealPlans: false,
      showCODEQ: false,
      displayDIEMScores: false,
    },

    // Sign Up Options
    signUpOptions: {
      enableSignUp: false,
      hideEmail: false,
      hidePhone: false,
      enableCodeBasedLogin: false,
      requirePasswordExpiration: false,
    },

    // Completion Options
    completionOptions: {
      showShareWidget: false,
      finishButtonLabel: 'Finish',
      signOutOnCompletion: false,
      externalLinkOnCompletion: '',
      showCompletionScreen: false,
      completionScreenContent: '',
    },
  });

  const [tempTheme, setTempTheme] = useState({});

  const [sections, setSections] = useState([
    { name: 'Theme Options', open: false },
    { name: 'Customization Options', open: false },
    { name: 'Sign Up Options', open: false },
    { name: 'Completion Options', open: false },
  ]);

  const handleAccordionChange = (sectionName) => (event, isExpanded) => {
    setSections((prevSections) =>
      prevSections.map((section) => (section.name === sectionName ? { ...section, open: isExpanded } : section)),
    );
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNestedChange = (section, field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [section]: {
        ...prevData[section],
        [field]: value,
      },
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // console.log('Partner settings submitted:', formData);
  };

  return (
    <Screen
      title="Partner Settings"
      showSidebarNav={true}
      admin={true}
    >
      <Box sx={{ display: 'flex', padding: 2 }}>
        <Box sx={{ flex: 1, marginRight: '220px' }}>
          <Box
            component="form"
            sx={{ padding: 2, marginBottom: 2 }}
            onSubmit={handleSubmit}
          >
            <Typography
              variant="h4"
              sx={{ marginBottom: 2 }}
            >
              Partner Settings
            </Typography>

            <Typography
              variant="body1"
              sx={{ marginBottom: 2 }}
            >
              Partner Name: {partnerObject.name || 'N/A'}
            </Typography>
            <Typography
              variant="body1"
              sx={{ marginBottom: 2 }}
            >
              User Role: {partnerObject.userRole || 'N/A'}
            </Typography>

            <Grid
              container
              spacing={2}
            >
              {sections.map((section, index) => (
                <Grid
                  item
                  xs={12}
                  key={index}
                >
                  <ControlledAccordion
                    title={section.name}
                    description={`Configure the ${section.name.toLowerCase()} for your account`}
                    contents={
                      {
                        'Theme Options': (
                          <ThemeOptions
                            formData={formData}
                            handleChange={handleChange}
                            setTempTheme={setTempTheme}
                          />
                        ),
                        'Customization Options': (
                          <CustomizationOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                            tempTheme={tempTheme}
                          />
                        ),
                        'Sign Up Options': (
                          <SignUpOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                          />
                        ),
                        'Completion Options': (
                          <CompletionOptions
                            formData={formData}
                            handleNestedChange={handleNestedChange}
                          />
                        ),
                      }[section.name]
                    }
                    expanded={section.open}
                    onChange={handleAccordionChange(section.name)}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ marginTop: 4 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
              >
                Save Settings
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Screen>
  );
};

export default PartnerSettingsScreen;
